// GlobalCallContext.js
// ─────────────────────────────────────────────────────────────────────────
// A single, app-wide voice-call session that lives ABOVE the router so the
// call — and the RetellWebClient driving it — survives route changes. This is
// what lets the Android/iOS app auto-start a test call on /fast-step and keep
// the agent talking as the user navigates, with a persistent banner on top
// (see GlobalCallBanner).
//
// SCOPE: intended for the native React Native WebView (Android/iOS) only. The
// provider itself is harmless on web (it just never gets a startGlobalCall()
// call there), but call sites gate on detectPlatform() before starting.
//
// Deliberately NO auto-stop timer: the call runs until the user ends it (or
// the agent hangs up). That is the whole point — the agent should keep talking
// across route changes.
// ─────────────────────────────────────────────────────────────────────────
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { RetellWebClient } from "retell-client-js-sdk";
import { trackEvent } from "../lib/analytics";
import { markSetupStep } from "../lib/setupScore";
import detectPlatform from "../utils/detectPlatform";
import { isPythonBackend, startPythonTestCall } from "../utils/apiBackend";
import {
  isNativeApp,
  requestNativePermission,
  PermissionStatus,
} from "../utils/nativePermissions";
import { useOfferStore, bumpSessionCallCount } from "../Store/offerStore";

const GlobalCallContext = createContext(null);
const ENDED_BANNER_STORAGE_KEY = "globalCallBannerEnded";

//sss stopCall() ke baad Retell ke `call_ended` event ka itna intezaar karte hain.
// Utne me na aaye to hum khud terminal state pe chale jate hain — warna banner
// "ending" pe atka reh jata hai (red button + chalti hui wave). Normal call me
// event isse bahut pehle aa jata hai, to ye kabhi fire hi nahi hota.
const END_CALL_WATCHDOG_MS = 4000;

// Hu-ba-hu wahi context is window ke andar dobara aaye to drop kar do. Ek tool
// call ka context sirf EK baar jana chahiye — dekho injectContext.
const INJECT_DEDUPE_MS = 4000;

// An ended call has no live audio session to restore, but its compact banner
// should survive a same-tab page refresh. Session storage clears with the tab.
const readEndedBanner = () => {
  try {
    const saved = sessionStorage.getItem(ENDED_BANNER_STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (_e) {
    return null;
  }
};

// Hook for any component to read call state / drive the call.
export const useGlobalCall = () => {
  const ctx = useContext(GlobalCallContext);
  if (!ctx) {
    // Provider missing — return a safe no-op shape so consumers never crash.
    return {
      callStatus: "idle",
      isAgentTalking: false,
      agentMeta: null,
      isCallLive: false,
      startGlobalCall: () => {},
      primeGlobalCall: () => {},
      endGlobalCall: () => {},
      injectContext: async () => false,
      awaitAgentIdle: async () => {},
      setMicMuted: () => false,
      isMicMuted: false,
      getCallAnalyser: () => null,
      getMicAnalyser: () => null,
      goLiveVariant: null,
      setGoLiveVariant: () => {},
    };
  }
  return ctx;
};

// True only inside the native Android/iOS WebView shell.
export const isMobileAppSurface = () => {
  // Unified flow: the mobile app flow (global call, banner, offer/confirm_go_live
  // gating, etc.) now runs on EVERY surface — web included — so web behaves the
  // same as the app. To restore per-platform behaviour, revert to:
  //   const p = detectPlatform(); return p === "android" || p === "ios";
  return true;
};

export const GlobalCallProvider = ({ children }) => {
  const [restoredEndedBanner] = useState(readEndedBanner);
  // callStatus: "idle" | "ready" | "connecting" | "active" | "ending" | "ended"
  //
  // "ready" = banner primed, koi call NAHI chal rahi. Dashboard ke "Test Agent
  // Call" se aane par yahi chahiye: call band rahe, par banner dikhe taaki user
  // wahin se green button dabakar call shuru kar sake. "idle" pe banner render
  // hi nahi hota, aur "ended" jhooth hota (koi call hui hi nahi) — isliye alag
  // state.
  const [callStatus, setCallStatus] = useState(
    restoredEndedBanner ? "ended" : "idle"
  );
  const [isAgentTalking, setIsAgentTalking] = useState(false);
  // Synchronous mirror of isAgentTalking so async flows (e.g. "wait for the
  // agent to finish its line before redirecting to checkout") can read it.
  const isAgentTalkingRef = useRef(false);
  // { agentId, agentName, agentAvatar, businessName }
  const [agentMeta, setAgentMeta] = useState(restoredEndedBanner);
  const agentMetaRef = useRef(restoredEndedBanner);
  // Which /recommended-plan offer is currently on screen: "payg" | "trial" |
  // null (not decided yet). Set by GoLiveVariant on mount so the live-call
  // route guidance injects the MATCHING context instead of a stale value.
  const [goLiveVariant, setGoLiveVariant] = useState(null);
  // True once the USER has spoken during the current call (first user
  // utterance). Drives the /fast-step popup → small-banner transition.
  const [userHasSpoken, setUserHasSpoken] = useState(false);
  // Mic silenced while a payment form is on screen — see setMicMuted below.
  const [isMicMuted, setIsMicMuted] = useState(false);

  const clientRef = useRef(null);
  const startingRef = useRef(false);
  const micStreamRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const callIdRef = useRef(null);
  // Python flow: WebSocket test call ka handle (hang-up ke liye).
  const pythonCallRef = useRef(null);
  const audioUnlockHandlerRef = useRef(null);
  // Aakhri inject ka content + waqt — duplicate drop karne ke liye.
  const lastInjectRef = useRef({ content: null, at: 0 });
  // Watchdog for the "ending" → "ended" transition. Sirf Retell ka `call_ended`
  // event hi terminal state tak le jata hai — aur wo event kabhi-kabhi aata hi
  // nahi (client null, call pehle hi remote se end ho chuki, socket drop). Tab
  // banner hamesha ke liye "ending" pe atak jata hai: red button + chalti wave.
  const endWatchdogRef = useRef(null);
  // True jab banner sirf "primed" hai (dashboard se aaye, call shuru nahi hui).
  // Start fail hone par isse pata chalta hai ki "idle" nahi, "ready" pe lautna
  // hai — warna banner gayab ho jata hai.
  const primedRef = useRef(false);
  // Our own AudioContext + analyser for the USER's mic, so the waveform reacts
  // to the user's voice too (the SDK's analyser only covers the agent audio).
  const audioCtxRef = useRef(null);
  const micAnalyserRef = useRef(null);

  const setupMicAnalyser = useCallback((stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx || !stream) return;
      const ctx = new Ctx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser); // NOT to destination — avoids mic echo
      audioCtxRef.current = ctx;
      micAnalyserRef.current = analyser;
    } catch (e) {
      console.warn("mic analyser setup failed:", e?.message);
    }
  }, []);

  const teardownMicAnalyser = useCallback(() => {
    micAnalyserRef.current = null;
    try {
      audioCtxRef.current?.close?.();
    } catch (_e) {
      /* ignore */
    }
    audioCtxRef.current = null;
  }, []);

  // Remove any armed first-gesture audio-unlock listeners.
  const disarmAudioUnlock = useCallback(() => {
    const h = audioUnlockHandlerRef.current;
    if (h) {
      document.removeEventListener("pointerdown", h);
      document.removeEventListener("touchstart", h);
      document.removeEventListener("click", h);
      audioUnlockHandlerRef.current = null;
    }
  }, []);

  // If the browser blocked autoplay for the auto-started call (no user gesture
  // yet), resume WebRTC audio playback on the very first user interaction —
  // any tap/click anywhere makes the agent audible.
  const armAudioUnlockOnGesture = useCallback(() => {
    disarmAudioUnlock(); // clear any previous arm first
    const resume = () => {
      const c = clientRef.current;
      if (!c?.startAudioPlayback) return;
      // Only stop listening once playback ACTUALLY resumes. A tap that lands
      // while the call is still connecting can't start audio yet — keep the
      // listener armed so the next tap (call now active) succeeds. This is
      // what removes the "needs two taps" behaviour.
      c.startAudioPlayback()
        .then(() => disarmAudioUnlock())
        .catch(() => {
          /* not ready yet — stay armed for the next gesture */
        });
    };
    audioUnlockHandlerRef.current = resume;
    document.addEventListener("pointerdown", resume);
    document.addEventListener("touchstart", resume);
    document.addEventListener("click", resume);
  }, [disarmAudioUnlock]);

  /*
   * Mute/unmute the user's mic WITHOUT touching the WebRTC session.
   *
   * Used by the embedded checkout sheet: while the payment form is on screen a
   * customer may well read their card number out loud, and an unmuted mic sends
   * that straight into Retell's transcript. That is a compliance problem the
   * old full-page redirect never had (the page — and the call — were gone).
   *
   * `track.enabled = false` silences the audio but keeps the track and the peer
   * connection alive, so unmuting is instant and the agent can keep talking to
   * the user throughout.
   */
  const setMicMuted = useCallback((muted) => {
    const stream = micStreamRef.current;
    if (!stream) return false;
    try {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !muted;
      });
      setIsMicMuted(!!muted);
      return true;
    } catch (e) {
      console.warn("[GlobalCall] setMicMuted failed:", e?.message);
      return false;
    }
  }, []);

  // Stop the mic tracks we opened for this call.
  const releaseMic = useCallback(() => {
    if (micStreamRef.current) {
      try {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch (_e) {
        /* ignore */
      }
      micStreamRef.current = null;
    }
    // The stream is gone, so "muted" is meaningless now — reset it, or the next
    // call starts with the UI claiming a mute that no longer applies.
    setIsMicMuted(false);
    teardownMicAnalyser();
  }, [teardownMicAnalyser]);

  // Lazily create the RetellWebClient once and wire its events. We keep ONE
  // client for the app's lifetime so a call can span many route changes.
  const ensureClient = useCallback(() => {
    if (clientRef.current) return clientRef.current;

    const client = new RetellWebClient();

    client.on("call_started", () => {
      setCallStatus("active");
      // Auto-start has no user gesture, so the browser may suspend WebRTC
      // audio output — the agent "talks" (events fire) but is inaudible.
      // Resume playback immediately; in the native WebView (autoplay allowed)
      // this succeeds and the agent is heard from the first word.
      client.startAudioPlayback?.().catch(() => {
        // Blocked (no gesture). Now that the call is genuinely ACTIVE, arm the
        // unlock so the user's very next tap resumes audio on this live call.
        armAudioUnlockOnGesture();
      });
    });

    client.on("call_ended", () => {
      // Event aa gaya — watchdog ki ab zaroorat nahi.
      if (endWatchdogRef.current) {
        clearTimeout(endWatchdogRef.current);
        endWatchdogRef.current = null;
      }
      // Asli call ho chuki — ab "ended" sach hai, "ready" pe lautne ki zaroorat
      // nahi.
      primedRef.current = false;
      // Preserve a terminal UI state so the banner can acknowledge the call
      // ending instead of disappearing immediately.
      setCallStatus("ended");
      // Reveal the offer pay buttons now that a real call has happened + ended.
      try {
        useOfferStore.getState().markCallEnded();
        // Reload-safe fallback: remember this agent has now had a call.
        bumpSessionCallCount(agentMetaRef.current?.agentId);
      } catch (_e) {
        /* ignore */
      }
      try {
        sessionStorage.setItem(
          ENDED_BANNER_STORAGE_KEY,
          JSON.stringify(agentMetaRef.current || {})
        );
      } catch (_e) {
        /* storage unavailable; banner remains for this render */
      }
      setIsAgentTalking(false);
      releaseMic();
      disarmAudioUnlock();
      callIdRef.current = null;
      const durationSec = callStartTimeRef.current
        ? Math.round((Date.now() - callStartTimeRef.current) / 1000)
        : 0;
      callStartTimeRef.current = null;
      trackEvent("test_call_complete", {
        duration_sec: durationSec,
        surface: "global_banner",
      });
    });

    // Drives the talking-wave animation in the banner.
    client.on("agent_start_talking", () => {
      isAgentTalkingRef.current = true;
      setIsAgentTalking(true);
    });
    client.on("agent_stop_talking", () => {
      isAgentTalkingRef.current = false;
      setIsAgentTalking(false);
    });

    client.on("error", (err) => {
      console.error("GlobalCall Retell error:", err);
      try {
        client.stopCall();
      } catch (_e) {
        /* ignore */
      }
      // Yahan bhi terminal state pahunch gayi — watchdog ko chalne mat do.
      if (endWatchdogRef.current) {
        clearTimeout(endWatchdogRef.current);
        endWatchdogRef.current = null;
      }
      setCallStatus("ended");
      setIsAgentTalking(false);
      isAgentTalkingRef.current = false;
      releaseMic();
      disarmAudioUnlock();
    });

    clientRef.current = client;
    return client;
  }, [releaseMic, disarmAudioUnlock, armAudioUnlockOnGesture]);

  // Banner ko dikhao BINA call shuru kiye. Dashboard ke "Test Agent Call" se
  // aane par yahi chahiye — call band, par banner maujood ho taaki user wahin
  // se call chala sake. Sirf "idle" se hi prime karte hain: chalti hui ya
  // khatam ho chuki call ka status isse overwrite nahi hona chahiye.
  const primeGlobalCall = useCallback((meta = {}) => {
    if (!meta.agentId) return;
    primedRef.current = true;
    setCallStatus((s) => (s === "idle" ? "ready" : s));
    agentMetaRef.current = meta;
    setAgentMeta(meta);
    /*
     * Primed banner ko bhi refresh ke paar zinda rakho.
     *
     * Ye state — banner dikh raha hai par call shuru nahi hui — sirf React
     * memory me thi. Refresh pe provider "idle" se shuru hota aur
     * GlobalCallBanner ka `if (callStatus === "idle") return null` banner
     * gayab kar deta. /recommended-plan pe yahi ho raha tha: $5 wali screen
     * dikhti thi par upar ka call banner nahi.
     *
     * Restore hone par status "ended" ban jaata hai, "ready" nahi. User ke
     * liye dono ek jaise hain — canStartCall dono me true hai, to green
     * call-again button waise hi milta hai.
     */
    try {
      sessionStorage.setItem(
        ENDED_BANNER_STORAGE_KEY,
        JSON.stringify(meta || {})
      );
    } catch (_e) {
      /* storage unavailable — refresh pe banner restore nahi hoga, bas */
    }
  }, []);

  // Start fail hua (mic deny, minutes khatam, network). Banner primed tha to
  // "ready" pe wapas jao — "idle" pe jane se banner gayab ho jata hai aur user
  // ke paas dobara try karne ka koi button hi nahi bachta.
  const revertStartFailure = useCallback(() => {
    setCallStatus(primedRef.current ? "ready" : "idle");
  }, []);

  // Start a global web call for the given agent. Safe to call multiple times —
  // it no-ops if a call is already starting or live.
  const startGlobalCall = useCallback(
    async (meta = {}) => {
      // "ready" bhi allowed — banner primed hai par call abhi shuru nahi hui.
      const startable =
        callStatus === "idle" || callStatus === "ready" || callStatus === "ended";
      if (startingRef.current || !startable) return;
      if (!meta.agentId) {
        console.warn("startGlobalCall: missing agentId");
        return;
      }

      startingRef.current = true;
      /*
       * Banner meta call ke SHURU me hi likh dete hain, sirf end pe nahi.
       *
       * Pehle yahan removeItem tha. Uska natija: call chalte hue refresh
       * karne par storage me kuch bacha hi nahi tha, to provider "idle" se
       * shuru hota aur GlobalCallBanner ka `if (callStatus === "idle")
       * return null` chal jaata — banner poora gayab.
       *
       * Live call refresh survive kar hi nahi sakti (WebRTC connection, mic
       * stream, SDK client — sab memory me hain), aur ye fix use zinda karne
       * ki koshish bhi nahi karta. Ye sirf itna karta hai ki refresh ke baad
       * user "ended" banner dekhe — apne agent ka naam aur green call-again
       * button ke saath — bajaye ek khaali screen ke jahan call dobara shuru
       * karne ka koi rasta hi na dikhe.
       *
       * Graceful end pe yahi key dobara likhi jaati hai (usi meta ke saath),
       * to woh raasta pehle jaisa hi hai.
       */
      try {
        sessionStorage.setItem(
          ENDED_BANNER_STORAGE_KEY,
          JSON.stringify(meta || {})
        );
      } catch (_e) {
        /* storage unavailable — refresh pe banner restore nahi hoga, bas */
      }
      agentMetaRef.current = meta;
      setAgentMeta(meta);
      setCallStatus("connecting");

      // ── Native app: grant the OS mic permission FIRST, via the native
      // bridge, and WAIT for it. Without this the auto-start's getUserMedia
      // is the thing that triggers the OS prompt — and it rejects on that
      // first attempt because the prompt hasn't been answered yet ("call
      // didn't start; worked on the second tap"). In a plain browser this
      // returns "unavailable" instantly and we fall through to getUserMedia,
      // which prompts as usual.
      if (isNativeApp()) {
        try {
          const status = await requestNativePermission("MICROPHONE", {
            agent: meta.agentName || "your agent",
            app: "Rexpt",
          });
          if (
            status === PermissionStatus.DENIED ||
            status === PermissionStatus.BLOCKED
          ) {
            trackEvent("test_call_start", {
              mic_permission: "denied",
              surface: "global_banner",
            });
            console.warn("GlobalCall: native mic permission not granted:", status);
            revertStartFailure();
            startingRef.current = false;
            return;
          }
        } catch (e) {
          // Bridge hiccup — fall through and let getUserMedia try anyway.
          console.warn("GlobalCall: native mic request failed, falling through", e);
        }
      }

      // Mic stream — same as the per-screen flows. Enable echo cancellation /
      // noise suppression so the agent's own speaker audio isn't captured and
      // transcribed back (which made the agent loop/echo itself).
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        micStreamRef.current = stream;
        setupMicAnalyser(stream); // user-voice reactive waveform
        trackEvent("test_call_start", {
          mic_permission: "granted",
          surface: "global_banner",
        });
      } catch (err) {
        trackEvent("test_call_start", {
          mic_permission: "denied",
          surface: "global_banner",
        });
        console.error("GlobalCall mic denied:", err);
        revertStartFailure();
        startingRef.current = false;
        return;
      }

      // ── Python flow (rexpt.in / port 3001): Retell/Node create-web-call ke
      // bajaye Python WebSocket se call. Agent Python backend me hai, Node me
      // nahi. Ye GlobalCallContext hi web (desktop) pe test call chalata hai. ──
      if (isPythonBackend()) {
        // GlobalCallContext apna mic le chuka; Python call apna khud leta hai —
        // is probe stream ko band kar do (double mic na khule).
        try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_e) { /* ignore */ }
        pythonCallRef.current = startPythonTestCall(meta.agentId, {
          onStatus: (s) => {
            if (s === "connected") {
              callStartTimeRef.current = Date.now();
              // Setup Score: testCallCompleted — dashboard ka "Test Agent Call"
              // (free user) isi banner call se chalta hai, jahan Python call pe
              // ye mark hota hi nahi tha. FastSetup ki Python call jaisa hi.
              markSetupStep("testCallCompleted", meta.agentId);
              // [FIX: python-call-status] Flip the UI from "connecting" to the
              // live state, exactly as the Retell SDK's on-active does for a Node
              // agent (setCallStatus("active") above). Without this the Python
              // call stayed on "connecting" forever — the End Call button never
              // appeared because callStatus never left "connecting".
              setCallStatus("active");
            } else if (s === "ended") {
              // The AGENT ended the call (voice "cut the call" -> worker.cancel()
              // -> WS close -> onStatus("ended")). This is DIFFERENT from the user
              // pressing End Call: there, endGlobalCall() runs settleEnded() and
              // hangs up. Here nothing else moves the status off "active", so the
              // banner + "End Call" button stayed red forever. Settle to "ended"
              // ourselves so the UI resets. (The user-hangup path already ran
              // settleEnded, and settleEnded is safe to hit once — callStatus is
              // "ending"/"ended" by then and the button is already gone.)
              pythonCallRef.current = null;
              settleEnded();
            } else if (s === "error") {
              revertStartFailure();
            }
          },
        });
        startingRef.current = false;
        return;
      }

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/agent/create-web-call`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ agent_id: meta.agentId }),
          }
        );

        if (res.status === 403) {
          // Plan minutes exhausted — bail cleanly.
          console.warn("GlobalCall: agent plan minutes exhausted");
          revertStartFailure();
          releaseMic();
          return;
        }

        const data = await res.json();
        // Keep the live call_id so we can drive Retell's live-call APIs
        // (e.g. make the agent speak before checkout).
        callIdRef.current = data?.call_id || null;
        // emitRawAudioSamples:true makes the SDK build analyzerComponent for the
        // AGENT audio — that's what drives the voice-reactive waveform (bars move
        // only while the agent is actually speaking).
        await ensureClient().startCall({
          accessToken: data.access_token,
          callId: data.call_id,
          transport: data.transport,
          iceServers: data.ice_servers,
          emitRawAudioSamples: true,
        });
        callStartTimeRef.current = Date.now();
        // Try to start audio output right away (native WebView / autoplay-allowed
        // contexts), and arm a first-gesture fallback for browsers that block
        // autoplay without a user tap.
        clientRef.current?.startAudioPlayback?.().catch(() => {});
        armAudioUnlockOnGesture();
      } catch (err) {
        console.error("GlobalCall start failed:", err);
        revertStartFailure();
        releaseMic();
      } finally {
        startingRef.current = false;
      }
    },
    [callStatus, ensureClient, releaseMic, armAudioUnlockOnGesture, setupMicAnalyser]
  );

  // Provider unmount pe pending watchdog clear — warna wo hatt chuke component
  // pe setState karne ki koshish karega.
  useEffect(() => {
    return () => {
      if (endWatchdogRef.current) {
        clearTimeout(endWatchdogRef.current);
        endWatchdogRef.current = null;
      }
    };
  }, []);

  // Call ko terminal state pe le jao — banner green "Call again" dikhaye aur
  // wave band ho. call_ended handler aur watchdog dono yahi shakl chahte hain.
  const settleEnded = useCallback(() => {
    // Asli call ho chuki — "ended" sach hai, "ready" pe lautna nahi hai.
    primedRef.current = false;
    setCallStatus("ended");
    setIsAgentTalking(false);
    isAgentTalkingRef.current = false;
    releaseMic();
    disarmAudioUnlock();
    callIdRef.current = null;
    callStartTimeRef.current = null;
    // call_ended handler ye bhi karta hai — watchdog wale raste pe chhoot na
    // jayen, warna offer buttons chhupe rehte hain aur reload pe ended banner
    // gayab ho jata hai.
    try {
      useOfferStore.getState().markCallEnded();
      bumpSessionCallCount(agentMetaRef.current?.agentId);
    } catch (_e) {
      /* ignore */
    }
    try {
      sessionStorage.setItem(
        ENDED_BANNER_STORAGE_KEY,
        JSON.stringify(agentMetaRef.current || {})
      );
    } catch (_e) {
      /* storage unavailable; banner remains for this render */
    }
  }, [releaseMic, disarmAudioUnlock]);

  // End the live global call. The client's call_ended handler resets state.
  const endGlobalCall = useCallback(async () => {
    // "ready" me koi call chal hi nahi rahi — end karne ko kuch nahi.
    if (
      callStatus === "idle" ||
      callStatus === "ready" ||
      callStatus === "ending" ||
      callStatus === "ended"
    ) {
      return;
    }
    setCallStatus("ending");

    // "ended" tak pahunchne ka ekmatra raasta call_ended event hai. Wo event na
    // aaye — client null ho (`?.` chupchaap undefined deta hai, na throw na
    // event), call pehle hi remote se end ho chuki ho, ya socket gir jaye — to
    // status hamesha "ending" pe atka rehta hai, aur banner red button + chalti
    // wave dikhata rehta hai. Ye watchdog usi soorat me terminal state pe le
    // jata hai. Event time pe aa gaya to ye cancel ho jata hai.
    if (endWatchdogRef.current) clearTimeout(endWatchdogRef.current);
    endWatchdogRef.current = setTimeout(() => {
      endWatchdogRef.current = null;
      settleEnded();
    }, END_CALL_WATCHDOG_MS);

    // Python flow: koi Retell client nahi — Python WebSocket call band karo.
    if (isPythonBackend() && pythonCallRef.current) {
      try { pythonCallRef.current.hangUp(); } catch (_e) { /* ignore */ }
      pythonCallRef.current = null;
      if (endWatchdogRef.current) {
        clearTimeout(endWatchdogRef.current);
        endWatchdogRef.current = null;
      }
      settleEnded();
      return;
    }

    try {
      await clientRef.current?.stopCall();
    } catch (err) {
      console.error("GlobalCall stop failed:", err);
      // Preserve terminal state ("ended") so banner stays visible with "Call again" button
      if (endWatchdogRef.current) {
        clearTimeout(endWatchdogRef.current);
        endWatchdogRef.current = null;
      }
      settleEnded();
    }
  }, [callStatus, settleEnded]);

  // Inject free-text context into the LIVE call so the agent speaks on demand
  // (e.g. "thank you, complete the payment and come back" right before we send
  // the user to Stripe). Proxied through the backend, which holds the Retell
  // API key. Resolves true if the injection was accepted. No-ops when there is
  // no live call (e.g. on web).
  const injectContext = useCallback(async (content) => {
    const callId = callIdRef.current;
    if (!callId || !content) {
      console.warn("[GlobalCall] injectContext skipped — no live call_id", {
        callId,
        hasContent: !!content,
      });
      return false;
    }

    /*
     * Same content, itni der ke andar dobara → drop.
     *
     * Ek tool call ka context BILKUL ek baar jana chahiye. Duplicate ke kai
     * raaste hain (socket event do baar aa jaye, effect do baar chale, ek hi
     * screen ke do caller), aur har raste ko alag-alag pakadna nazuk hai.
     * Ye aakhri gate hai: agar hu-ba-hu wahi text abhi-abhi bheja gaya hai to
     * dobara mat bhejo.
     *
     * Window chhoti rakhi hai — koi bhi asli flow ek hi text ko itni jaldi
     * dobara nahi bhejta; jo dobara bhejta hai (jaise user ka dobara action)
     * uska text alag hota hai ya der baad aata hai.
     */
    const now = Date.now();
    if (
      lastInjectRef.current.content === content &&
      now - lastInjectRef.current.at < INJECT_DEDUPE_MS
    ) {
      console.warn("[GlobalCall] injectContext skipped — duplicate within",
        INJECT_DEDUPE_MS, "ms");
      return true; // pehli koshish ja chuki hai — caller ke liye ye success hi hai
    }
    lastInjectRef.current = { content, at: now };

    try {
      console.log("[GlobalCall] update-live-call →", { callId, content });
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/agent/update-live-call`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ call_id: callId, content }),
        }
      );
      console.log(res,"res")
      const body = await res.json().catch(() => null);
      console.log("[GlobalCall] update-live-call ←", res.status, body);
      return res.ok;
    } catch (err) {
      console.error("[GlobalCall] injectContext failed:", err);
      return false;
    }
  }, []);

  // ── Native refresh/back guard bridge ───────────────────────────────────────
  // In the mobile WebView `beforeunload` does NOT reliably show a confirm dialog,
  // so the "refreshing will end the call" prompt has to live on the NATIVE side.
  // We keep native informed of whether a call is live by posting the status on
  // every change; the native shell holds that flag and, when the user tries to
  // refresh / go back / pull-to-refresh while a call is live, shows its own
  // Alert. On confirm it either reloads the WebView or posts {type:"END_CALL"}
  // back to us (handled below) so the call ends gracefully first.
  useEffect(() => {
    if (!window.ReactNativeWebView) return;
    try {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "CALL_STATUS",
          status: callStatus, // "idle" | "connecting" | "active" | "ending"
          live:
            callStatus === "connecting" ||
            callStatus === "active" ||
            callStatus === "ending",
        })
      );
    } catch (_e) {
      /* ignore */
    }
  }, [callStatus]);

  // Refresh / close-tab guard for the GLOBAL (native-flow) call. FastSetup's
  // own beforeunload is keyed on its LOCAL web-client flags, which are never set
  // for a global call — so that confirm never fired during the sales/global
  // call. Key this one on the GLOBAL callStatus instead. In a normal browser
  // this pops the native "Reload site?" confirm; inside the RN WebView the
  // dialog won't render (that path is the native bridge above), but it's
  // harmless there.


  // Native asks us to end the call (e.g. the user confirmed the refresh/back
  // Alert). End gracefully; native can then safely reload the WebView.
  useEffect(() => {
    const onNativeMessage = (event) => {
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type === "END_CALL") endGlobalCall();
      } catch (_e) {
        /* not our message — ignore */
      }
    };
    // iOS delivers on window, Android on document.
    window.addEventListener("message", onNativeMessage);
    document.addEventListener("message", onNativeMessage);
    return () => {
      window.removeEventListener("message", onNativeMessage);
      document.removeEventListener("message", onNativeMessage);
    };
  }, [endGlobalCall]);

  // Resolve once the agent has FINISHED its current/next line — so a redirect
  // (e.g. to Stripe checkout) doesn't cut it off. Handles both cases:
  //   • agent already talking → wait until it stops,
  //   • just injected a line (not talking yet) → a short grace lets it START,
  //     then we wait for it to stop.
  // maxMs caps the total wait so a stuck "talking" state never blocks forever.
  const awaitAgentIdle = useCallback((maxMs = 20000, graceMs = 1000) => {
    return new Promise((resolve) => {
      const start = performance.now();
      let sawTalking = isAgentTalkingRef.current;
      const check = () => {
        const elapsed = performance.now() - start;
        if (isAgentTalkingRef.current) sawTalking = true;
        if (elapsed >= maxMs) return resolve(); // hard cap
        if (isAgentTalkingRef.current) {
          // still speaking — keep waiting
        } else if (sawTalking) {
          return resolve(); // spoke, and has now finished
        } else if (elapsed >= graceMs) {
          return resolve(); // never started within the grace window
        }
        setTimeout(check, 120);
      };
      check();
    });
  }, []);

  const value = {
    callStatus,
    isAgentTalking,
    agentMeta,
    isCallLive:
      callStatus === "connecting" ||
      callStatus === "active" ||
      callStatus === "ending",
    startGlobalCall,
    primeGlobalCall,
    endGlobalCall,
    injectContext,
    awaitAgentIdle,
    setMicMuted,
    isMicMuted,
    // Live audio analysers for a real, voice-reactive waveform:
    //   agent → RetellWebClient's analyser (needs emitRawAudioSamples:true)
    //   mic   → our own analyser on the user's mic stream
    getCallAnalyser: () =>
      clientRef.current?.analyzerComponent?.analyser || null,
    getMicAnalyser: () => micAnalyserRef.current || null,
    goLiveVariant,
    setGoLiveVariant,
  };

  return (
    <GlobalCallContext.Provider value={value}>
      {children}
    </GlobalCallContext.Provider>
  );
};

export default GlobalCallContext;
